/* eslint-disable camelcase */

import React, { useState, useEffect, createContext } from 'react';
import PropTypes from 'prop-types';
import { PseudoStates, useFeatures, useSession } from '@cordata/buffet';
import { all, spread } from 'axios';
import { differenceInMonths, parse, distanceInWordsStrict } from 'date-fns';

import {
  authorizedInstance as rb,
  authorizedPyInstance as py,
} from '~/utils/api/instances';
import { MatchStatus } from '~/utils/const/identity';

import log from '~/utils/event/log';

import service from './service';
import isCaseTeamMember from './case-team-membership';

const PatientContext = createContext([]);

const PatientProvider = ({ children, patient_id }) => {
  const { isAuthed, current } = useSession();
  const { hasAppIdentity } = useFeatures();
  const [status, setStatus] = useState('loading');
  const [patient, setPatient] = useState({});
  const [rand, setRand] = useState(0);

  const audit = action =>
    log.create({
      patientUid: patient_id,
      action,
    });

  const changeProfilePicture = v =>
    service.changeProfilePicture(v, patient_id);

  const buildMatchData = (matchResults) => {
    const calcDisputed = matchResult =>
      matchResult.matchStatusId !== MatchStatus.Approved &&
        matchResult.matchStatusId !== MatchStatus.Rejected;

    const metadata = {
      totalMatches: matchResults.length,
      totalDisputed: matchResults.filter(calcDisputed).length,
      hasAnyDisputes: matchResults.some(calcDisputed),
      hasAllDisputes: matchResults.every(calcDisputed),
      canDispute: matchResults.filter(mr => mr.matchStatusId === MatchStatus.Approved).length > 0,
    };

    const groupedMatches = matchResults.reduce((acc, obj) => {
      const key = obj.sourceDescription;
      if (!acc[key]) {
        acc[key] = {
          hasDisputed: false,
          matches: [],
        };
      }
      acc[key].matches.push(obj);
      if (obj.matchStatusId !== MatchStatus.Approved &&
          obj.matchStatusId !== MatchStatus.Rejected) {
        acc[key].hasDisputed = true;
      }
      return acc;
    }, {});

    return {
      ...metadata,
      matches: matchResults,
      matchesBySource: groupedMatches,
    };
  };

  const fetchPatient = () => {
    if (isAuthed && patient_id) {
      if (!hasAppIdentity) {
        all([
          rb
            .get(`/patients/${patient_id}`),
          rb
            .get(`/patients/${patient_id}/current-inbound-referral`),
        ])
          .then(spread((p, ibr) => {
            setPatient({
              ...p.data.data,
              currentInboundReferral: {
                ...ibr.data.data,
                ...ibr.data.data.attributes,
              },
            });

            setStatus('success');
          }));
      }

      if (hasAppIdentity) {
        all([
          py
            .get('/api/data/public/identity_search', { params: { patient_uid: patient_id } }),
          py
            .get('/api/data/public/list-view-cs-tbl-source-child', { params: { patient_uid: patient_id } }),
          py
            .get('/api/data/public/list_view_cs_tbl_patientpreferrednames', { params: { patient_uid: patient_id } }),
          py
            .get('/api/data/public/list_view_cs_tbl_patientflags', { params: { 'patientflag__!': 'Custody Alert (legal docs or confirmed CSB)', patient_uid: patient_id } }),
          py
            .get('/api/data/public/list-view-cs-tbl-source-case', { params: { patient_uid: patient_id, 'all-data': '1' } }),
        ])
          .then(spread((search, sourceChild, preferredNames, patientFlags, srcCase) => {
            const s = search.data.data[0]?.attributes;
            const sc_uid = sourceChild.data.data[0]?.id;
            const sc = sourceChild.data.data[0]?.attributes;
            const pN = preferredNames.data.data;
            const pF = patientFlags.data.data;
            const sourceCases = srcCase.data.data;

            const isCaseTeamMemberForPatient = isCaseTeamMember(
              sourceCases,
              current.attributes,
            );

            py
              .get(
                '/api/data/public/list-view-cs-tbl-matchresults',
                { params: { sourcepersonid__eq: sc.sacwisPersonId } },
              ).then(({ data: { data: matchResultsData } }) => {
                const matchResults = matchResultsData.length
                  ? matchResultsData.map(m => m.attributes)
                  : [];
                setPatient({
                  ...s,
                  ...sc,
                  adaptiveAge: distanceInWordsStrict(parse(sc.birthDate), new Date(), { unit: differenceInMonths(new Date(), sc.birthDate) <= 24 ? 'M' : 'Y' }),
                  matchResults: buildMatchData(matchResults),
                  preferredNames: pN,
                  patientFlags: pF.length ? pF.map(m => ({ id: m.id, ...m.attributes })) : [],
                  isCaseTeamMember: isCaseTeamMemberForPatient,
                  sourceCases: sourceCases.length
                    ? sourceCases.map(m => ({ id: m.id, ...m.attributes }))
                    : [],
                });

                setStatus('success');
                py.get(`/api/data/public/list-view-cs-tbl-source-child/${sc_uid}`);
                /* after success ping pyapi to set patient as "viewed" */
              });
          }));
      }
    }
  };

  useEffect(() => {
    setStatus('loading');
    setPatient({});
    fetchPatient();
  }, [isAuthed, hasAppIdentity, patient_id, rand]);

  return (
    <PatientContext.Provider
      value={[
        status,
        {
          id: patient_id,
          ...patient,
        }, {
          refresh: () =>
            setRand(Math.random()),
          audit,
          changeProfilePicture,
        },
      ]}
    >
      {status === 'success' ?
        children
       : (
         <PseudoStates status={status} />
      )}
    </PatientContext.Provider>
  );
};

PatientProvider.propTypes = {
  children: PropTypes.node.isRequired,
  patient_id: PropTypes.string.isRequired,
};

export {
  PatientContext,
  PatientProvider,
};
